import { ALLOW_AVAILABILITY_FALLBACK, requiredAmountForLocation, resolveLocationCensus } from "../../utils/censusResolution";
import type { DepartmentLocation } from "../../utils/models";
import { StatsMode } from "../../utils/models";

/** One area's share of a day's RN recommendation. */
export type RecommendationPart = { unit: string; nurses: number };

/**
 * Split the day's total nurse count into one number per area (ICU, Telemetry).
 *
 * The server only sends one total, so we work the split out here from the ratio ladder and the
 * day's census — the same way the Staffing page does, so the two always agree.
 *
 * Rounds per location and per job to match the backend (`_ladder_nurses_for_shift`).
 * Returns [] when there's no ladder to split by; the caller then shows just the total.
 */
export const recommendationBreakdown = ({
	locations,
	byDimensions,
	totalCensus,
	statsMode,
	shiftType,
	dayAbbr,
	score,
}: {
	locations: DepartmentLocation[];
	byDimensions: Record<string, number>;
	totalCensus: number;
	statsMode: StatsMode;
	shiftType: string;
	dayAbbr: string;
	score: number;
}): RecommendationPart[] => {
	// Only the ratio ladder knows the per-area need. PPD/HPPD gives one number for the whole dept.
	if (statsMode === StatsMode.PPD_HPPD) return [];

	const byUnit = new Map<string, number>();
	const unitOrder: string[] = [];
	for (const location of locations) {
		if (location.shift_type !== shiftType || !location.days?.includes(dayAbbr)) continue;
		const unit = location.unit?.trim();
		// No area on this location, so we can't put its nurses in either bucket. Skip it.
		if (!unit) continue;

		const census = resolveLocationCensus(location, byDimensions, totalCensus, statsMode, ALLOW_AVAILABILITY_FALLBACK);
		// One filter per job, so each job rounds separately.
		const jobIds = [...new Set(location.ratioLadder?.map((rung) => rung.jobId).filter((id): id is string => !!id) ?? [])];
		const filters = jobIds.length ? jobIds.map((jobId) => ({ kind: "job" as const, jobId })) : [{ kind: "all" as const }];

		let nurses = 0;
		for (const filter of filters) {
			nurses += Math.floor(requiredAmountForLocation(location, census, statsMode, dayAbbr, filter) * score + 0.5);
		}
		if (nurses > 0) {
			if (!byUnit.has(unit)) unitOrder.push(unit);
			byUnit.set(unit, (byUnit.get(unit) ?? 0) + nurses);
		}
	}

	// Keep the order the locations arrived in, so rows don't jump around as the numbers change.
	// This order is NOT meaningful — the locations query has no ORDER BY. Callers that need to know
	// which area is which must match on the unit name (see sidebarAreaSplits).
	return unitOrder.filter((unit) => byUnit.has(unit)).map((unit) => ({ unit, nurses: byUnit.get(unit) as number }));
};

/** The area the ICU rules apply to, matched against a location's configured `unit`. */
const ICU_UNIT_NAME = "icu";
/** ICU census = its nurses x 1.5 (rounded up). */
const ICU_PATIENTS_PER_NURSE = 1.5;
/** ICU patients are 50% sicker, so its score is 1.5x the other area's. */
const ICU_WLS_MULTIPLIER = 1.5;

/** One area's share of a sidebar metric, already formatted for display. */
export type AreaSplit = { unit: string; value: string };

/** ICU gets `ceil(nurses x 1.5)` patients, Telemetry the rest. Clamped so neither goes negative. */
export const splitCensus = (census: number, icuNurses: number): { icu: number; other: number } => {
	const icu = Math.min(census, Math.ceil(icuNurses * ICU_PATIENTS_PER_NURSE));
	return { icu, other: Math.max(0, census - icu) };
};

/**
 * Split the patient score so ICU is 1.5x the other area, AND the two still average back to the
 * total shown above them (weighted by how many patients each area has).
 *
 * Returns null when there are no patients to weight by.
 */
export const splitScore = (score: number, icuCensus: number, otherCensus: number): { icu: number; other: number } | null => {
	const weighted = icuCensus * ICU_WLS_MULTIPLIER + otherCensus;
	if (weighted <= 0) return null;
	const other = (score * (icuCensus + otherCensus)) / weighted;
	return { icu: other * ICU_WLS_MULTIPLIER, other };
};

/** Split a range like "1-3" between the areas, by their share of patients. Always sums to the total. */
export const splitRange = (low: number, high: number, icuCensus: number, otherCensus: number): { icu: string; other: string } | null => {
	const total = icuCensus + otherCensus;
	if (total <= 0) return null;
	const share = (n: number) => {
		const icu = Math.round((n * icuCensus) / total);
		return { icu, other: Math.max(0, n - icu) };
	};
	const lo = share(low);
	const hi = share(high);
	const fmt = (a: number, b: number) => (a === b ? `${a}` : `${a}-${b}`);
	return { icu: fmt(lo.icu, hi.icu), other: fmt(lo.other, hi.other) };
};

/** All the per-area numbers the sidebar shows for one day. */
export type SidebarAreaSplits = {
	units: [string, string];
	census: AreaSplit[];
	admissions: AreaSplit[];
	discharges: AreaSplit[];
	score: AreaSplit[];
	nurses: AreaSplit[];
};

/**
 * Work out every per-area line the sidebar shows for one day.
 *
 * Only works for a department with exactly two areas — that's what the ICU/Telemetry rules assume.
 * Anything else returns null and the sidebar just shows its totals.
 */
export const sidebarAreaSplits = ({
	day,
	shiftType,
	locations,
	statsMode,
}: {
	day: {
		census_low?: number;
		census_high?: number;
		score?: number;
		staffNo?: number;
		census_grouping?: { by_dimensions?: Record<string, number> };
		date?: { toFormat: (f: string) => string };
		shiftEvents?: { numberAdmissionsLow?: number; numberAdmissionsHigh?: number; numberDischargesLow?: number; numberDischargesHigh?: number };
	};
	shiftType: string;
	locations: DepartmentLocation[];
	statsMode: StatsMode;
}): SidebarAreaSplits | null => {
	const nurses = recommendationBreakdown({
		locations,
		byDimensions: day.census_grouping?.by_dimensions ?? {},
		totalCensus: day.census_high ?? 0,
		statsMode,
		shiftType,
		dayAbbr: day.date?.toFormat("ccc").toLowerCase() ?? "",
		score: day.score ?? 1,
	});
	// The rules only make sense with two areas.
	if (nurses.length !== 2) return null;

	// Pick ICU by name, not by position: the locations query has no ORDER BY, so either area can
	// come back first and Telemetry would otherwise drive the ICU census/score math and colors.
	const icuIndex = nurses.findIndex((part) => part.unit.trim().toLowerCase() === ICU_UNIT_NAME);
	if (icuIndex === -1) return null;
	const [icu, other] = icuIndex === 0 ? [nurses[0], nurses[1]] : [nurses[1], nurses[0]];

	const [icuUnit, otherUnit] = [icu.unit, other.unit];
	const censusLow = splitCensus(day.census_low ?? 0, icu.nurses);
	const censusHigh = splitCensus(day.census_high ?? 0, icu.nurses);
	const score = splitScore(day.score ?? 0, censusHigh.icu, censusHigh.other);
	const admissions = splitRange(day.shiftEvents?.numberAdmissionsLow ?? 0, day.shiftEvents?.numberAdmissionsHigh ?? 0, censusHigh.icu, censusHigh.other);
	const discharges = splitRange(day.shiftEvents?.numberDischargesLow ?? 0, day.shiftEvents?.numberDischargesHigh ?? 0, censusHigh.icu, censusHigh.other);

	const range = (low: number, high: number) => (low === high ? `${low}` : `${low}-${high}`);
	const pair = (icu: string, other: string): AreaSplit[] => [
		{ unit: icuUnit, value: icu },
		{ unit: otherUnit, value: other },
	];

	return {
		units: [icuUnit, otherUnit],
		census: pair(range(censusLow.icu, censusHigh.icu), range(censusLow.other, censusHigh.other)),
		admissions: admissions ? pair(admissions.icu, admissions.other) : [],
		discharges: discharges ? pair(discharges.icu, discharges.other) : [],
		score: score ? pair(score.icu.toFixed(2), score.other.toFixed(2)) : [],
		nurses: pair(`${icu.nurses}`, `${other.nurses}`),
	};
};
